import { Link } from "@tanstack/react-router";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, object, string, number } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { RxType } from "@/types/enums";
import { FertilizerProduct, FertilizerType } from "@/types/products";
import { ListRx, Rx, TargetRateUnits } from "@/types/rx";
import { ManagementZones } from "@/types/managementZones";
import { DataLayer } from "@/types/dataLayer";
import { Imagery } from "@/types/imagery";
import { Pump, SystemType } from "@/types/pump";
import {
    getPumpFormValue,
    getPumpIdFromFormValue,
} from "@/lib/utils/pump";
import { Button, Accordion, AccordionItem } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faArrowLeft,
    faArrowUpFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateStringMMDDYYYY } from "@/lib/utils/date";
import TextInput from "@/components/inputs/TextInput";
import SelectInput from "@/components/inputs/SelectInput";
import SectionedSelectInput from "@/components/inputs/SectionedSelectInput";
import SectionedAutoCompleteInput from "@/components/inputs/SectionedAutoComplete";
import FileInput from "@/components/inputs/FileInput";
import NumberInput from "@/components/inputs/NumberInput";
import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { FieldDashboardContextType } from "@/lib/fieldDashboard/FieldDashboardContext";
import {
    getIrrigationSystemDepth,
    getIrrigationSystemSpeed,
} from "@/types/irrigationSystem";
import { debounce } from "lodash";
import { UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { AxiosRequestConfig } from "axios";

const schema = object().shape({
    field: number().required().label("Field"),
    irrigation_system: number().nullable().label("Irrigation System"),
    name: string().required().label("Name"),
    rx_type: string().required().oneOf(Object.values(RxType)).label("Rx Type"),
    template: string().nullable().label("Template"),
    target_rate_attribute: string()
        .label("Target Rate Attribute")
        .when("template_type", {
            is: (value: string) => value === "data_layer",
            then: (targetRateAttribute) => targetRateAttribute.required(),
            otherwise: (targetRateAttribute) => targetRateAttribute.nullable(),
        }),
    target_rate_units: string()
        .label("Target Rate Units")
        .when("template_type", {
            is: (value: string) => value === "data_layer",
            then: (targetRateUnits) => targetRateUnits.required(),
            otherwise: (targetRateUnits) => targetRateUnits.nullable(),
        }),
    fertilizer_product: string().required().label("Fertilizer Product"),
    pump: string()
        .label("Pump")
        .when("rx_type", {
            is: (value: RxType) => value === RxType.VRF,
            then: (pump) => pump.required(),
            otherwise: (pump) => pump.nullable(),
        }),
    depth_inches: number()
        .min(0)
        .label("Depth")
        .when("rx_type", {
            is: (value: RxType) => value === RxType.VRF,
            then: (depthInches) => depthInches.required(),
            otherwise: (depthInches) => depthInches.nullable(),
        }),
    speed_percentage: number()
        .min(0)
        .max(100)
        .label("Speed")
        .when("rx_type", {
            is: (value: RxType) => value === RxType.VRF,
            then: (speedPercentage) => speedPercentage.required(),
            otherwise: (speedPercentage) => speedPercentage.nullable(),
        }),
    max_speed_percentage: number()
        .min(0)
        .max(100)
        .label("Max Speed")
        .when("rx_type", {
            is: (value: RxType) => value === RxType.VRI,
            then: (maxSpeedPercentage) => maxSpeedPercentage.required(),
            otherwise: (maxSpeedPercentage) => maxSpeedPercentage.nullable(),
        }),
    injection_rate_gal_per_hour: number()
        .min(0)
        .label("Injection Rate")
        .when("rx_type", {
            is: (value: RxType) => value === RxType.VRI,
            then: (injectionRateGalPerHour) =>
                injectionRateGalPerHour.required(),
            otherwise: (injectionRateGalPerHour) =>
                injectionRateGalPerHour.nullable(),
        }),
    total_fertilizer: number().nullable(),
    total_application_time: number().nullable(),
});

const unitOptions = [
    {
        description: "lbs N/acre",
        id: TargetRateUnits.PoundsNPerAcre,
    },
    {
        description: "gal/acre",
        id: TargetRateUnits.GallonsPerAcre,
    },
    {
        description: "lbs/acre",
        id: TargetRateUnits.PoundsPerAcre,
    },
];

type FormType = InferType<typeof schema>;

function RxForm({
    rx,
    updateRxRequest,
    setFertilizerType,
}: {
    rx?: Rx;
    updateRxRequest?: UseMutationResult<Rx, Error, AxiosRequestConfig>;
    setFertilizerType?: (fertilizerType: FertilizerType) => void;
}) {
    const { t } = useTranslation();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { fieldQuery } =
        useFieldDashboard() ?? ({} as FieldDashboardContextType);
    const { fieldId } = useParams({
        strict: false,
    });
    const navigate = useNavigate();
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            field: Number(fieldId),
            rx_type: RxType.ZONE,
            fertilizer_product: "2",
            depth_inches: 0.5,
            max_speed_percentage: 100,
        },
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const [
        rxType,
        template,
        depth,
        speed,
        irrigationSystemId,
        name,
        fertilizer_product,
    ] = form.watch([
        "rx_type",
        "template",
        "depth_inches",
        "speed_percentage",
        "irrigation_system",
        "name",
        "fertilizer_product",
    ]);
    const irrigationSystem = useMemo(() => {
        if (!fieldQuery?.data?.irrigation_systems) return null;
        if (!irrigationSystemId) {
            return fieldQuery.data.irrigation_systems[0];
        } else {
            return fieldQuery.data.irrigation_systems.find(
                (system) => system.id === Number(irrigationSystemId),
            );
        }
    }, [irrigationSystemId, fieldQuery?.data?.irrigation_systems]);
    useEffect(() => {
        if (!irrigationSystem) return;
        if (speed) {
            form.setValue(
                "depth_inches",
                getIrrigationSystemDepth(irrigationSystem, speed),
                {
                    shouldValidate: false,
                    shouldDirty: false,
                },
            );
        }
    }, [irrigationSystem, form, speed]);
    useEffect(() => {
        if (!irrigationSystem) return;
        if (depth) {
            form.setValue(
                "speed_percentage",
                getIrrigationSystemSpeed(irrigationSystem, depth),
                {
                    shouldValidate: false,
                    shouldDirty: false,
                },
            );
        }
    }, [irrigationSystem, form, depth]);
    useEffect(() => {
        if (!irrigationSystem) return;
        if (irrigationSystem?.pump) {
            form.setValue(
                "pump",
                `${irrigationSystem.pump.id}-${irrigationSystem.pump.system_type}`,
                {
                    shouldValidate: false,
                    shouldDirty: false,
                },
            );
        }
    }, [irrigationSystemId, irrigationSystem, form]);

    const resetForm = useCallback(
        (data: Rx, pump?: Pump) => {
            const currentPumpValue = getPumpFormValue(form.getValues("pump"));
            const pumpFormValue = pump
                ? `${pump.id}-${pump.system_type}`
                : currentPumpValue ??
                (irrigationSystem?.pump
                    ? `${irrigationSystem.pump.id}-${irrigationSystem.pump.system_type}`
                    : undefined);

            form.reset({
                field: data.field,
                irrigation_system: data.irrigation_system,
                name: data.name,
                template:
                    data?.template_id && data?.template_type
                        ? `${data.template_id}-${data.template_type}`
                        : undefined,
                target_rate_attribute: data.target_rate_attribute,
                target_rate_units: data.target_rate_units?.toString(),
                rx_type: data.rx_type,
                fertilizer_product: data.fertilizer_product?.toString(),
                depth_inches: data.depth_inches,
                speed_percentage: data.speed_percentage,
                injection_rate_gal_per_hour: data.injection_rate_gal_per_hour,
                max_speed_percentage: data.max_speed_percentage,
                total_fertilizer: Math.ceil(data.total_fertilizer),
                total_application_time: data.total_application_time,
                pump: pumpFormValue,
            });
        },
        [form, irrigationSystem],
    );
    useEffect(() => {
        if (rx) {
            resetForm(rx);
        }
    }, [rx, resetForm]);

    const getFertilizers = useGetRequest<FertilizerProduct[]>({
        url: `/fertilizers/`,
    });
    const fertilizerOptions = useMemo(() => {
        const coreProducts = getFertilizers.data
            ?.filter((fertilizer) => Boolean(fertilizer.core_product))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((fertilizer) => ({
                value: fertilizer.id,
                label: fertilizer.name,
            })) ?? [];
        const dryFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Dry,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const liquidFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Liquid,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const pgrProducts = getFertilizers.data
            ?.filter(
                (fertilizer) =>
                    fertilizer.fertilizer_type === FertilizerType.PlantGrowthRegulator,
            )
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((fertilizer) => ({
                value: fertilizer.id,
                label: fertilizer.name,
            })) ?? [];
        const harvestAidProducts = getFertilizers.data
            ?.filter(
                (fertilizer) =>
                    fertilizer.fertilizer_type === FertilizerType.HarvestAid,
            )
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((fertilizer) => ({
                value: fertilizer.id,
                label: fertilizer.name,
            })) ?? [];
        const favoriteProducts = getFertilizers.data
            ?.filter((fertilizer) => Boolean(fertilizer.is_favorite))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((fertilizer) => ({
                value: fertilizer.id,
                label: fertilizer.name,
            })) ?? [];
        return [RxType.VRF, RxType.VRI].includes(rxType)
            ? [
                {
                    value: "favorite",
                    label: "Favorite Products",
                    options: favoriteProducts,
                },
                {
                    value: "core",
                    label: "Core Products",
                    options: coreProducts,
                },
                {
                    value: FertilizerType.Liquid,
                    label: "Liquid Fertilizer",
                    options: liquidFertilizers,
                },
            ]
            : [
                {
                    value: "favorite",
                    label: "Favorite Products",
                    options: favoriteProducts,
                },
                {
                    value: "core",
                    label: "Core Products",
                    options: coreProducts,
                },
                {
                    value: FertilizerType.Dry,
                    label: "Dry Fertilizer",
                    options: dryFertilizers,
                },
                {
                    value: FertilizerType.Liquid,
                    label: "Liquid Fertilizer",
                    options: liquidFertilizers,
                },
                {
                    value: FertilizerType.PlantGrowthRegulator,
                    label: "Plant Growth Regulator",
                    options: pgrProducts,
                },
                {
                    value: FertilizerType.HarvestAid,
                    label: "Harvest Aid",
                    options: harvestAidProducts,
                },
            ];
    }, [getFertilizers.data, rxType]);
    const selectedFertilizer = useMemo(() => {
        return getFertilizers.data?.find(
            (fertilizer) => fertilizer.id === Number(fertilizer_product),
        );
    }, [getFertilizers.data, fertilizer_product]);
    useEffect(() => {
        if (selectedFertilizer) {
            setFertilizerType?.(selectedFertilizer.fertilizer_type as FertilizerType);
        }
    }, [selectedFertilizer, setFertilizerType]);
    const getManagementZones = useGetRequest<ManagementZones[]>({
        url: `/fields/${fieldId}/management_zones/`,
    });
    const getDataLayers = useGetRequest<DataLayer[]>({
        url: `/fields/${fieldId}/data_layers/`,
    });
    const getFavoriteImagery = useGetRequest<Imagery[]>({
        url: `/fields/${fieldId}/imagery/favorites/`,
    });
    const templateOptions = useMemo(() => {
        const managementLayerOptions =
            getManagementZones.data?.map((zone) => ({
                label: zone.name,
                value: zone.id,
            })) ?? [];
        const dataLayerOptions =
            getDataLayers.data?.map((layer) => ({
                label: layer.name,
                value: layer.id,
            })) ?? [];
        const imageryOptions =
            getFavoriteImagery.data?.map((imagery) => ({
                label: `${formatDateStringMMDDYYYY(imagery.capture_date)} Image`,
                value: imagery.id,
            })) ?? [];
        return [
            {
                value: "management_layer",
                label: "Management Zones",
                options: managementLayerOptions,
            },
            {
                value: "data_layer",
                label: "Data Layers",
                options: dataLayerOptions,
            },
            {
                value: "imagery",
                label: "Favorite Imagery",
                options: imageryOptions,
            },
        ];
    }, [getManagementZones.data, getDataLayers.data, getFavoriteImagery.data]);
    const getPumps = useGetRequest<Pump[]>({
        url: `/pumps/`,
        params: {
            grower: fieldQuery.data?.grower,
        },
        enabled: !!fieldQuery.data?.grower,
    });
    const pumpOptions = useMemo(() => {
        const constantPumps =
            getPumps.data
                ?.filter((pump) => pump.system_type === SystemType.constantRate)
                .map((pump) => ({
                    value: pump.id,
                    label: `${pump.pump_make ?? ""} ${pump.pump_model ? pump.pump_model : "Pump"}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) ?? [];
        const variablePumps =
            getPumps.data
                ?.filter((pump) => pump.system_type === SystemType.variableRate)
                .map((pump) => ({
                    value: pump.id,
                    label: `${pump.pump_make} ${pump.pump_model}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) ?? [];
        return rxType === RxType.VRF
            ? [
                {
                    value: SystemType.variableRate,
                    label: "Variable Rate Pumps",
                    options: variablePumps,
                },
            ]
            : [
                {
                    value: SystemType.constantRate,
                    label: "Constant Rate Pumps",
                    options: constantPumps,
                },
                {
                    value: SystemType.variableRate,
                    label: "Variable Rate Pumps",
                    options: variablePumps,
                },
            ];
    }, [getPumps.data, rxType]);
    const updatePumpRequest = useMutateRequest<Rx>({
        method: "put",
        url: `/prescriptions/${rx?.id}/pump/`,
        successMessage: t("success.updated_pump", { ns: "messages" }),
        errorMessage: t("error.error_updating_pump", { ns: "messages" }),
    });
    const autoUpdatePump = () => {
        if (!rx?.id) return;
        const pumpId = getPumpIdFromFormValue(form.getValues("pump"));
        const pump = getPumps.data?.find((p) => p.id === pumpId);
        updatePumpRequest.mutate(
            {
                data: {
                    pump_id: pumpId,
                },
            },
            {
                onSuccess: (updatedRx) => {
                    if (updatedRx) {
                        fieldQuery?.refetch();
                        resetForm(updatedRx, pump);
                    }
                },
            },
        );
    };

    const uploadFileRequest = useMutateRequest<DataLayer>({
        method: "post",
        url: `/fields/${fieldId}/data_layers/`,
        successMessage: t("success.file_uploaded_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.error_uploading_file", { ns: "messages" }),
    });

    const createRxRequest = useMutateRequest<ListRx>({
        method: "post",
        url: "/prescriptions/",
        successMessage: t("success.rx_created_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.error_creating_rx", { ns: "messages" }),
    });
    const createRx = (data: FormType) => {
        createRxRequest.mutate(
            {
                data: {
                    ...data,
                    field: Number(fieldId),
                    irrigation_system: irrigationSystem?.id,
                    template_id: Number(data.template?.split("-")[0]),
                    template_type: data.template?.split("-")[1],
                    fertilizer_product: Number(data.fertilizer_product),
                    target_rate_units: Number(data.target_rate_units),
                },
            },
            {
                onSuccess: (newRx) => {
                    if (newRx) {
                        navigate({
                            to: `/dashboard/fields/${fieldId}/rx/${newRx.id}`,
                        });
                    }
                },
            },
        );
    };

    const autosaveRx = debounce(async () => {
        const isFormValid = await form.trigger();
        if (!rx?.id || !isFormValid) {
            return;
        }
        const data = form.getValues();
        updateRxRequest?.mutate(
            {
                data: {
                    ...data,
                    field: Number(fieldId),
                    template_id: data.template
                        ? Number(data.template.split("-")[0])
                        : null,
                    template_type: data.template
                        ? data.template.split("-")[1]
                        : null,
                    fertilizer_product: Number(data.fertilizer_product),
                    target_rate_units: Number(data.target_rate_units),
                    injection_rate_gal_per_hour: Number(
                        data.injection_rate_gal_per_hour,
                    ),
                },
            },
            {
                onSuccess: (updatedRx) => {
                    if (updatedRx) {
                        resetForm(updatedRx);
                    }
                },
            },
        );
    }, 500);
    const [formIsExpanded, setFormIsExpanded] = useState(true);

    const { formatUnit } = useI18n();

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(createRx)}>
                <Accordion
                    isCompact={true}
                    defaultExpandedKeys={["rx-form"]}
                    onExpandedChange={(set) => {
                        if (set.has("rx-form")) {
                            setFormIsExpanded(true);
                        } else {
                            setFormIsExpanded(false);
                        }
                    }}
                >
                    <AccordionItem
                        key="rx-form"
                        title={formIsExpanded ? "" : name}
                        startContent={
                            <Button
                                size="sm"
                                as={Link}
                                to="/dashboard/fields/$fieldId/rx"
                                variant="light"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                {t("rx_list", { ns: "rx" })}
                            </Button>
                        }
                    >
                        <div className="flex flex-col w-full h-full gap-2 p-2">
                            <div className="flex flex-row w-full gap-2 items-center">
                                <TextInput
                                    name="name"
                                    label={t("name", { ns: "rx" })}
                                    required={true}
                                    onAutosave={autosaveRx}
                                    size="sm"
                                    fullWidth
                                />
                                <Button
                                    color="secondary"
                                    isIconOnly
                                    size="lg"
                                    onPress={() => {
                                        if (navigator && navigator.share) {
                                            navigator.share({
                                                title: `${form.getValues("name")} Rx - ${fieldQuery.data?.name}`,
                                                text: `${form.getValues("name")} Rx - ${fieldQuery.data?.name}`,
                                                url: window.location.href,
                                            });
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowUpFromBracket}
                                    />
                                </Button>
                            </div>
                            <div className="flex flex-row w-full gap-2 items-center">
                                <SelectInput
                                    name="rx_type"
                                    label={t("rx_type", { ns: "rx" })}
                                    size="sm"
                                    options={Object.values(RxType).map(
                                        (type) => ({
                                            id: type,
                                            description: type,
                                        }),
                                    )}
                                    required={true}
                                    disabledKeys={
                                        !irrigationSystem
                                            ? [RxType.VRF, RxType.VRI]
                                            : []
                                    }
                                    onAutosave={autosaveRx}
                                    onSelectionChange={(e) => {
                                        if (
                                            e.currentKey !== RxType.ZONE &&
                                            selectedFertilizer?.fertilizer_type ===
                                            FertilizerType.Dry
                                        ) {
                                            form.setValue(
                                                "fertilizer_product",
                                                "",
                                            );
                                        }
                                    }}
                                />
                                <SectionedAutoCompleteInput
                                    name="fertilizer_product"
                                    label={t("fertilizer_product", {
                                        ns: "rx",
                                    })}
                                    groups={fertilizerOptions}
                                    required={true}
                                    size="sm"
                                    onAutosave={autosaveRx}
                                />
                            </div>
                            {[RxType.VRF, RxType.VRI].includes(rxType) && (
                                <SectionedSelectInput
                                    name="pump"
                                    label={t("pump", { ns: "rx" })}
                                    size="sm"
                                    groups={pumpOptions}
                                    required={rxType === RxType.VRF}
                                    onAutosave={autoUpdatePump}
                                />
                            )}
                            {rxType === RxType.VRF && (
                                <div className="flex flex-row w-full gap-2 items-center">
                                    <NumberInput
                                        name="depth_inches"
                                        label={t("depth", { ns: "rx" })}
                                        required={true}
                                        min={0}
                                        step={0.01}
                                        endContent="inches"
                                        size="sm"
                                        onAutosave={autosaveRx}
                                    />
                                    <NumberInput
                                        name="speed_percentage"
                                        label={t("speed", { ns: "rx" })}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        required={true}
                                        endContent="%"
                                        size="sm"
                                        onAutosave={autosaveRx}
                                    />
                                </div>
                            )}
                            {rxType === RxType.VRI && (
                                <div className="flex flex-row w-full gap-2 items-center">
                                    <NumberInput
                                        name="injection_rate_gal_per_hour"
                                        label={t("injection_rate", {
                                            ns: "rx",
                                        })}
                                        required={true}
                                        min={0}
                                        step={0.01}
                                        endContent={formatUnit("gph", "Lph")}
                                        size="sm"
                                        onAutosave={autosaveRx}
                                    />
                                    <NumberInput
                                        name="max_speed_percentage"
                                        label={t("max_speed", { ns: "rx" })}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        required={true}
                                        endContent="%"
                                        size="sm"
                                        onAutosave={autosaveRx}
                                    />
                                </div>
                            )}
                            <div className="flex flex-row w-full gap-2 items-top">
                                <SectionedSelectInput
                                    name="template"
                                    label={t("variable_rate_template", {
                                        ns: "rx",
                                    })}
                                    groups={templateOptions}
                                    description={t(
                                        "select_a_template_for_vr_zones_upload_your_own_rx_or_leave_blank_for_flat_rate_rx",
                                        { ns: "rx" },
                                    )}
                                    onAutosave={autosaveRx}
                                    showClearButton={true}
                                    clearButtonLabel={t("flat_rate", {
                                        ns: "rx",
                                    })}
                                    size="sm"
                                />
                                <FileInput
                                    label={t("upload_rx", { ns: "rx" })}
                                    isLoading={uploadFileRequest.isPending}
                                    onSelect={(file) => {
                                        if (file instanceof File) {
                                            const formData = new FormData();
                                            formData.append("file", file);
                                            formData.append("name", file.name);
                                            formData.append(
                                                "layer_type",
                                                "prescription",
                                            );
                                            uploadFileRequest.mutate(
                                                {
                                                    data: formData,
                                                },
                                                {
                                                    onSuccess: (data) => {
                                                        getDataLayers.refetch();
                                                        form.setValue(
                                                            "template",
                                                            `${data.id}-data_layer`,
                                                        );
                                                    },
                                                },
                                            );
                                        }
                                    }}
                                />
                            </div>
                            {template?.includes("data_layer") && (
                                <div className="flex flex-row w-full gap-2 items-center">
                                    <SelectInput
                                        name="target_rate_attribute"
                                        label={t("target_rate_attribute", {
                                            ns: "rx",
                                        })}
                                        options={
                                            getDataLayers.data
                                                ?.find(
                                                    (layer) =>
                                                        layer.id ===
                                                        Number(
                                                            template?.split(
                                                                "-",
                                                            )[0],
                                                        ),
                                                )
                                                ?.attributes.map(
                                                    (attribute) => ({
                                                        id: attribute.name,
                                                        description:
                                                            attribute.name,
                                                    }),
                                                ) ?? []
                                        }
                                        required={true}
                                        size="sm"
                                        onAutosave={autosaveRx}
                                    />
                                    <SelectInput
                                        name="target_rate_units"
                                        label={t("target_rate_units", {
                                            ns: "rx",
                                        })}
                                        options={unitOptions}
                                        required={true}
                                        size="sm"
                                        onAutosave={autosaveRx}
                                    />
                                </div>
                            )}
                            {!!rx?.id && rxType === RxType.ZONE && (
                                <NumberInput
                                    name="total_fertilizer"
                                    label={t("total_fertilizer", { ns: "rx" })}
                                    endContent={
                                        selectedFertilizer?.fertilizer_type ===
                                            FertilizerType.Dry
                                            ? formatUnit("lbs", "kg")
                                            : formatUnit("gals", "L")
                                    }
                                    isReadOnly={true}
                                />
                            )}
                            {!!rx?.id && rxType !== RxType.ZONE && (
                                <div className="flex flex-row w-full gap-2 items-center">
                                    <NumberInput
                                        name="total_fertilizer"
                                        label={t("total_fertilizer", {
                                            ns: "rx",
                                        })}
                                        isReadOnly={true}
                                        endContent={
                                            selectedFertilizer?.fertilizer_type ===
                                                FertilizerType.Dry
                                                ? formatUnit("lbs", "kg")
                                                : formatUnit("gals", "L")
                                        }
                                    />
                                    <NumberInput
                                        name="total_application_time"
                                        label={t("total_application_time", {
                                            ns: "rx",
                                        })}
                                        isReadOnly={true}
                                        endContent="hours"
                                    />
                                </div>
                            )}
                            {!rx?.id && (
                                <Button
                                    type="submit"
                                    color="secondary"
                                    variant="solid"
                                    className="mt-3"
                                    size="sm"
                                    isLoading={createRxRequest.isPending}
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                    {t("create_rx", { ns: "rx" })}
                                </Button>
                            )}
                        </div>
                    </AccordionItem>
                </Accordion>
            </form>
        </FormProvider>
    );
}

export default RxForm;
